import React, { Fragment, useEffect, useState, useCallback, useRef } from "react";
import ReactDOM from "react-dom";
import { useLocation } from "react-router-dom";
import Breadcrumb from "../../../layout/breadcrumb";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Form,
  FormGroup,
  Input,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import CommonDataTable from "../../../share-components/table/commonDataTable";
import { useDispatch, useSelector } from "react-redux";
import withReducer from "../../../store/withReducer";
import productReducer from "../../manage-product/store";
import DateRangePickerPrimary from "../../../share-components/ui/DateRangePickerPrimary";
import AsyncSelect from "react-select/async";
import Select, { components } from "react-select";
import { mapSearchAutoSuggestion } from "../../../utils/mapper";
import { sellerBrandSuggestionApi, getPriceReductionFilterOptionsApi, storePriceReductionsApi, exportPriceReductionStockApi } from "../../../services/manage-product/productService";
import { getProductTagApi } from "../../../services/manage-product/setting/productTagService";
import { getPriceReductionProducts } from "../../manage-product/store/prodcutSlice";
import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import ModalFormSubmit from "../../../share-components/form/modalFormSubmit";
import PreLoader from "../../../share-components/ui/preLoader";
import SettingForm from "../../manage-product/product-setting/settingForm";
import {
  addProductTag,
  closeEditDialog,
  openNewDialog,
} from "../../manage-product/store/productTagSlice";
import debounce from "lodash/debounce";
import ReactSwitch from "react-switch";
import { FaRobot } from "react-icons/fa";
import AiRecommendationDetailsModal from "./AiRecommendationDetailsModal";
import PriceReductionSummaryCards from "./PriceReductionSummaryCards";

const dataMenu = [
  {
    type: 0,
    text: "Price Reductions",
  },
];

const afterToday = () => {
  return (date) => date.getTime() > new Date().getTime();
};

const NOTES_TOOLTIP_WIDTH = 300;
const NOTES_TOOLTIP_MAX_HEIGHT = 240;
const HEADER_HEIGHT = 80; // approx sticky header height in px
const VIEWPORT_PADDING = 8;
const HOVER_CLOSE_DELAY = 150;
const CHECK_STOCK_MIN = 1;
const CHECK_STOCK_MAX = 9999;
const CHECK_STOCK_NO_RESULTS_ERROR = "No products available with the entered stock quantity.";

const NotesCell = ({ row }) => {
  const notes = row.notes;
  const hasNotes = notes && String(notes).trim() !== "";
  const iconRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const closeTimerRef = useRef(null);

  if (!hasNotes) {
    return <span style={{ color: "#999", fontSize: "11px" }}>—</span>;
  }

  const show = () => {
    clearTimeout(closeTimerRef.current);
    const rect = iconRef.current?.getBoundingClientRect();
    if (!rect) return;

    const minLeft = VIEWPORT_PADDING;
    const maxLeft = window.innerWidth - NOTES_TOOLTIP_WIDTH - VIEWPORT_PADDING;
    const left = Math.max(
      minLeft,
      Math.min(rect.left + rect.width / 2 - NOTES_TOOLTIP_WIDTH / 2, maxLeft)
    );
    const top = Math.max(rect.bottom + VIEWPORT_PADDING, HEADER_HEIGHT + VIEWPORT_PADDING);

    setPos({ top, left });
    setVisible(true);
  };

  const hide = () => {
    clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => setVisible(false), HOVER_CLOSE_DELAY);
  };

  return (
    <>
      <span
        ref={iconRef}
        style={{
          cursor: "default",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#2684ff",
          fontSize: "14px",
        }}
        onMouseEnter={show}
        onMouseLeave={hide}
      >
        <i className="fa fa-sticky-note-o" aria-hidden="true" />
      </span>
      {visible && ReactDOM.createPortal(
        <div
          onMouseEnter={() => clearTimeout(closeTimerRef.current)}
          onMouseLeave={hide}
          style={{
            position: "fixed",
            top: pos.top,
            left: pos.left,
            width: NOTES_TOOLTIP_WIDTH,
            maxHeight: NOTES_TOOLTIP_MAX_HEIGHT,
            overflowY: "auto",
            backgroundColor: "#fff",
            border: "1px solid #ddd",
            borderRadius: 4,
            padding: "8px 12px",
            boxShadow: "0 3px 12px rgba(0,0,0,0.18)",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            fontSize: 12,
            lineHeight: 1.5,
            zIndex: 9999,
          }}
        >
          {notes}
        </div>,
        document.body
      )}
    </>
  );
};

const ProductTagSelector = ({ 
  productId, 
  selectedTags, 
  tagOptions, 
  onTagChange, 
  onCreateTag,
  isDisabled = false
}) => {
  const customOptions = [
    {
      value: '__add_new__',
      label: 'Add new tag',
      isAddNew: true
    },
    ...(tagOptions || [])
  ];

  return (
    <div className="product-tag-selector-wrapper">
      <Select
        className="select2-filter product-tag-selector"
        isClearable
        isMulti
        isSearchable
        isDisabled={isDisabled}
        placeholder="Select Tag"
        value={selectedTags}
        options={customOptions}
      getOptionLabel={(option) => option.name || option.label || ''}
      getOptionValue={(option) => String(option.value || option.id || '')}
      isOptionEqualToValue={(option, value) => {
        const optionVal = String(option.value || option.id || '');
        const valueVal = String(value.value || value.id || '');
        return optionVal === valueVal && optionVal !== '';
      }}
      menuPortalTarget={document.body}
      styles={{
        control: (base, state) => ({
          ...base,
          minHeight: 38,
          height: 38,
          maxHeight: 38,
          border: '1px solid #e0e0e0',
          borderRadius: '4px',
          backgroundColor: state.isDisabled ? '#f5f5f5' : '#ffffff',
          boxShadow: 'none',
          cursor: state.isDisabled ? 'default' : 'pointer',
          overflow: 'visible',
          pointerEvents: state.isDisabled ? 'auto' : 'auto',
          '&:hover': {
            border: '1px solid #e0e0e0',
          },
        }),
        placeholder: (base) => ({
          ...base,
          color: '#9e9e9e',
          fontSize: '12px',
        }),
        input: (base) => ({
          ...base,
          margin: 0,
          padding: 0,
          fontSize: '12px',
        }),
        multiValue: (base) => ({
          ...base,
          backgroundColor: '#e0e0e0',
          borderRadius: '4px',
          fontSize: '12px',
          maxWidth: '180px',
          margin: '2px',
        }),
        multiValueLabel: (base) => ({
          ...base,
          color: '#333',
          fontSize: '12px',
          padding: '2px 6px',
        }),
        multiValueRemove: (base) => ({
          ...base,
          color: '#666',
          '&:hover': {
            backgroundColor: '#d0d0d0',
            color: '#333',
          },
        }),
        valueContainer: (base, state) => ({
          ...base,
          padding: '4px 12px',
          minHeight: '32px',
          maxHeight: '32px',
          height: '32px',
          overflowY: 'auto',
          overflowX: 'hidden',
          display: 'flex',
          flexWrap: 'wrap',
          WebkitOverflowScrolling: 'touch',
          pointerEvents: 'auto',
          cursor: state.isDisabled ? 'default' : 'text',
        }),
        indicatorsContainer: (base) => ({
          ...base,
          height: '32px',
        }),
        indicatorSeparator: () => ({
          display: 'none',
        }),
        dropdownIndicator: (base) => ({
          ...base,
          color: '#9e9e9e',
          padding: '4px 8px',
          '&:hover': {
            color: '#9e9e9e',
          },
        }),
        menuPortal: (base) => ({ ...base, zIndex: 100 }),
        menu: (base) => ({
          ...base,
          zIndex: 100,
        }),
        option: (base, state) => ({
          ...base,
          backgroundColor: state.isFocused ? '#f0f0f0' : state.isSelected ? '#e0e0e0' : '#ffffff',
          color: state.isSelected ? '#333' : state.isDisabled ? '#999' : '#666',
          fontSize: '12px',
          padding: '8px 12px',
          cursor: 'pointer',
          '&:active': {
            backgroundColor: state.isDisabled ? '#f0f0f0' : '#e0e0e0',
          },
        }),
      }}
      onChange={(selected) => {
        if (selected && Array.isArray(selected)) {
          const filtered = selected.filter(opt => opt.value !== '__add_new__' && opt.id !== '__add_new__');
          onTagChange(productId, filtered);
        } else {
          onTagChange(productId, selected || []);
        }
      }}
      components={{
        Option: (props) => {
          const { data, innerRef, innerProps } = props;
          if (data.value === '__add_new__' || data.id === '__add_new__') {
            return (
              <div
                ref={innerRef}
                {...innerProps}
                style={{
                  ...props.getStyles('option', props),
                  padding: '8px 12px',
                  cursor: 'pointer',
                  backgroundColor: props.isFocused ? '#f0f0f0' : '#ffffff',
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onCreateTag();
                  return false;
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#f0f0f0';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#ffffff';
                }}
              >
                <i className="fa fa-plus" style={{ marginRight: '8px', fontSize: '12px' }}></i>
                Add new tag
              </div>
            );
          }
          return <components.Option {...props} />;
        }
      }}
      />
    </div>
  );
};

const PriceReductions = () => {
  const dispatch = useDispatch();
  const authData = useSelector(({ authReducer }) => authReducer);
  const location = useLocation();
  
  const getProductIdsFromUrl = () => {
    const searchParams = new URLSearchParams(location.search);
    const productIdsParam = searchParams.get('product_ids');
    if (productIdsParam) {
      return productIdsParam.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
    }
    return [];
  };

  const getDiscountFromUrl = () => {
    const searchParams = new URLSearchParams(location.search);
    const discountParam = searchParams.get('discount');
    return discountParam || '';
  };

  const getVariantSku = (variant, productId, fallbackId = '') => {
    return variant.sku || `variant-${productId}-${variant.id || fallbackId}`;
  };

  const getVariantKey = (variant, productId, idx) => {
    return variant.id ? `variant-${productId}-${variant.id}-${idx}` : `variant-${productId}-${idx}`;
  };

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [checkStockError, setCheckStockError] = useState("");
  const [checkStockInput, setCheckStockInput] = useState("");

  const endDateObj = new Date();
  const startDateObj = new Date();
  startDateObj.setDate(endDateObj.getDate() - 90);
  const [dateRange, setDateRange] = useState([startDateObj, endDateObj]);

  const [filterSeller, setFilterSeller] = useState(
    authData?.defaultSeller || null
  );
  const [filterBrand, setFilterBrand] = useState(null);
  const [filterCategory, setFilterCategory] = useState(null);
  const [filterSortBy, setFilterSortBy] = useState(null);
  const [filterStatus, setFilterStatus] = useState(null);
  const [filterCheckStock, setFilterCheckStock] = useState(null);
  const [filterGrade, setFilterGrade] = useState(null);
  const [filterGeneratedBy, setFilterGeneratedBy] = useState(null);
  const [filterChannelStatus, setFilterChannelStatus] = useState(null);
  const [filterAdditionalStatuses, setFilterAdditionalStatuses] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [searchInputValue, setSearchInputValue] = useState("");

  const [isBrandMenuOpen, setIsBrandMenuOpen] = useState(false);
  const [isCategoryMenuOpen, setIsCategoryMenuOpen] = useState(false);
  const [isSortByMenuOpen, setIsSortByMenuOpen] = useState(false);
  const [isStatusMenuOpen, setIsStatusMenuOpen] = useState(false);
  const [isGradeMenuOpen, setIsGradeMenuOpen] = useState(false);
  const [isGeneratedByMenuOpen, setIsGeneratedByMenuOpen] = useState(false);
  const [isCheckStockFocused, setIsCheckStockFocused] = useState(false);
  const [statusOptions, setStatusOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [gradeOptions, setGradeOptions] = useState([]);
  const [generatedByOptions, setGeneratedByOptions] = useState([]);

  const [products, setProducts] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [priceReductionSummary, setPriceReductionSummary] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedVariants, setSelectedVariants] = useState({});
  const [variantDiscounts, setVariantDiscounts] = useState({});
  const [productTagOptions, setProductTagOptions] = useState([]);
  const [productSelectedTags, setProductSelectedTags] = useState({});
  const [expandedRows, setExpandedRows] = useState({});
  const [buttonDisable, setButtonDisable] = useState(false);
  const [pendingProductIdForNewTag, setPendingProductIdForNewTag] = useState(null);
  const [globalDiscount, setGlobalDiscount] = useState(getDiscountFromUrl());
  const [discountMode, setDiscountMode] = useState("manual");
  const [globalTags, setGlobalTags] = useState([]);
  const [sendLoading, setSendLoading] = useState(false);
  const [aiDetailsProduct, setAiDetailsProduct] = useState(null);

  const productTagDefaultValues = {
    id: '',
    name: '',
    tageName: '',
    publish_at: ''
  };

  const productTagSchema = yup.object().shape({
    name: yup.string().required('You must enter a product type name'),
  });

  const { handleSubmit: handleTagFormSubmit, formState: tagFormState, reset: resetTagForm, control: tagControl, register: registerTag, setValue: setTagValue, trigger: triggerTag, getValues: getTagValues } = useForm({
    mode: 'onChange',
    defaultValues: productTagDefaultValues,
    resolver: yupResolver(productTagSchema)
  });

  const { errors: tagErrors } = tagFormState;

  const customStyles = {
    control: (base) => ({
      ...base,
      minHeight: 50,
      border: "none",
    }),
    menuPortal: (base) => ({ ...base, zIndex: 100 }),
  };

  const sortOptions = [
    { value: "product_name-asc", label: "Product Name (A-Z)" },
    { value: "product_name-desc", label: "Product Name (Z-A)" },
    { value: "total_qty-asc", label: "Current Qty (Low to High)" },
    { value: "total_qty-desc", label: "Current Qty (High to Low)" },
    { value: "rrp-asc", label: "RRP (Low to High)" },
    { value: "rrp-desc", label: "RRP (High to Low)" },
    { value: "sale_price-asc", label: "Sale Price (Low to High)" },
    { value: "sale_price-desc", label: "Sale Price (High to Low)" },
    { value: "grade-asc", label: "Grade (A-Z)" },
    { value: "grade-desc", label: "Grade (Z-A)" },
    { value: "total_sold-asc", label: "Total Sold (Low to High)" },
    { value: "total_sold-desc", label: "Total Sold (High to Low)" },
    { value: "total_return-asc", label: "Total Returns (Low to High)" },
    { value: "total_return-desc", label: "Total Returns (High to Low)" },
  ];

  const channelStatusOptions = [
    { value: 1, label: "Goddiva Website Discount Only" },
    { value: 2, label: "Discount Across All Channels" },
    { value: 3, label: "Selected Marketplace Discount" },
  ];

  const additionalStatusOptions = [
    { value: "odd_size", label: "Odd Sizes and Broken Size Runs" },
    { value: "hold_price", label: "Hold Price and Increase Promotion" },
    { value: "final_clearance", label: "Final Clearance or Exit" },
  ];

  const brandPromiseOption = (inputValue) =>
    new Promise((resolve) => {
      const data = {
        value: inputValue,
        seller_id: filterSeller?.value,
      };
      setTimeout(() => {
        resolve(mapSearchAutoSuggestion(sellerBrandSuggestionApi(data)));
      }, 300);
    });

  const handleSelectProduct = (productId) => {
    const isCurrentlySelected = selectedProducts.includes(productId);
    
    if (isCurrentlySelected) {
      setSelectedProducts(prev => prev.filter((id) => id !== productId));
      setSelectedVariants(prev => {
        const newVariants = { ...prev };
        delete newVariants[productId];
        return newVariants;
      });
      setVariantDiscounts(prev => {
        const newDiscounts = { ...prev };
        delete newDiscounts[productId];
        return newDiscounts;
      });
    } else {
      const product = products.find(p => p.id === productId);
      const productVariants = product?.variants || product?.variants_objects || [];
      setSelectedProducts(prev => [...prev, productId]);
      
      if (productVariants.length > 0) {
        const variantSkus = productVariants.map(variant => getVariantSku(variant, productId));
        setSelectedVariants(prev => ({
          ...prev,
          [productId]: variantSkus
        }));
        setVariantDiscounts(prev => ({
          ...prev,
          [productId]: Object.fromEntries(variantSkus.map(sku => [sku, globalDiscount || '']))
        }));
      }
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedProducts(products.map(product => product.id));
      
      const allVariants = {};
      const allDiscounts = {};
      
      products.forEach(product => {
        const productVariants = product.variants || product.variants_objects || [];
        if (productVariants.length > 0) {
          const variantSkus = productVariants.map(variant => getVariantSku(variant, product.id));
          allVariants[product.id] = variantSkus;
          allDiscounts[product.id] = Object.fromEntries(variantSkus.map(sku => [sku, globalDiscount || '']));
        }
      });
      
      setSelectedVariants(allVariants);
      setVariantDiscounts(allDiscounts);
    } else {
      setSelectedProducts([]);
      setSelectedVariants({});
      setVariantDiscounts({});
    }
  };

  const handleSelectVariant = (productId, variantSku) => {
    const productVariants = selectedVariants[productId] || [];
    const isCurrentlySelected = productVariants.includes(variantSku);
    
    if (isCurrentlySelected) {
      setSelectedVariants(prev => ({
        ...prev,
        [productId]: productVariants.filter((sku) => sku !== variantSku),
      }));
      setVariantDiscounts(prev => {
        if (!prev[productId]) return prev;
        
        const { [variantSku]: _, ...restVariants } = prev[productId];
        
        if (Object.keys(restVariants).length === 0) {
          const { [productId]: __, ...restProducts } = prev;
          return restProducts;
        }
        
        return {
          ...prev,
          [productId]: restVariants
        };
      });
    } else {
      setSelectedVariants(prev => ({
        ...prev,
        [productId]: [...productVariants, variantSku],
      }));
      setVariantDiscounts(prev => ({
        ...prev,
        [productId]: {
          ...(prev[productId] || {}),
          [variantSku]: globalDiscount || '',
        },
      }));
    }
  };

  const handleDiscountChange = (productId, variantSku, value) => {
    setVariantDiscounts((prev) => ({
      ...prev,
      [productId]: {
        ...(prev[productId] || {}),
        [variantSku]: value,
      },
    }));
  };

  const ExpandedComponent = ({ data }) => {
    const productId = data.id;
    const variants = data.variants || data.variants_objects || [];
    const productSelectedVariants = selectedVariants[productId] || [];
    const productDiscounts = variantDiscounts[productId] || {};
    const [localDiscounts, setLocalDiscounts] = useState({});
    
    const getDisplayDiscount = (variantSku, isSelected) => {
      if (localDiscounts[variantSku] !== undefined) {
        return localDiscounts[variantSku];
      }
      const specificDiscount = productDiscounts[variantSku];
      if (specificDiscount !== undefined && specificDiscount !== '') {
        return specificDiscount;
      }
      if (isSelected && globalDiscount) {
        return globalDiscount;
      }
      return '';
    };

    return (
      <div style={{ padding: "20px", backgroundColor: "#f9f9f9" }}>
        <div style={{ backgroundColor: "#fff", borderRadius: "4px", overflow: "hidden" }}>
          <table style={{ width: "100%", fontSize: "11px", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ backgroundColor: "#f5f5f5", borderBottom: "1px solid #e0e0e0" }}>
                <th style={{ padding: "10px", textAlign: "center", fontWeight: "bold", color: "#68646d", fontSize: "12px", width: "30px" }}></th>
                <th style={{ padding: "10px", textAlign: "left", fontWeight: "bold", color: "#68646d", fontSize: "12px" }}>SKU</th>
                <th style={{ padding: "10px", textAlign: "center", fontWeight: "bold", color: "#68646d", fontSize: "12px" }}>Goddiva Qty</th>
                <th style={{ padding: "10px", textAlign: "center", fontWeight: "bold", color: "#68646d", fontSize: "12px" }}>CG Portal Qty</th>
                <th style={{ padding: "10px", textAlign: "center", fontWeight: "bold", color: "#68646d", fontSize: "12px" }}>Total Qty</th>
                <th style={{ padding: "10px", textAlign: "center", fontWeight: "bold", color: "#68646d", fontSize: "12px", minWidth: "100px" }}>Cost Price</th>
                <th style={{ padding: "10px", textAlign: "center", fontWeight: "bold", color: "#68646d", fontSize: "12px" }}>RRP</th>
                <th style={{ padding: "10px", textAlign: "center", fontWeight: "bold", color: "#68646d", fontSize: "12px" }}>Sale Price</th>
                <th style={{ padding: "10px", textAlign: "center", fontWeight: "bold", color: "#68646d", fontSize: "12px" }}>Sold</th>
                <th style={{ padding: "10px", textAlign: "center", fontWeight: "bold", color: "#68646d", fontSize: "12px" }}>Return</th>
                <th style={{ padding: "10px", textAlign: "center", fontWeight: "bold", color: "#68646d", fontSize: "12px" }}>Sale</th>
                <th style={{ padding: "10px", textAlign: "center", fontWeight: "bold", color: "#68646d", fontSize: "12px" }}>
                  {discountMode === "ai" ? "AI Recommended Discount(%)" : "Discount(%)"}
                </th>
                {discountMode === "ai" && (
                  <th style={{ padding: "10px", textAlign: "left", fontWeight: "bold", color: "#68646d", fontSize: "12px", minWidth: "240px" }}>Reason</th>
                )}
                <th style={{ padding: "10px", textAlign: "center", fontWeight: "bold", color: "#68646d", fontSize: "12px" }}>Price after Discount</th>
              </tr>
            </thead>
            <tbody>
              {variants.length > 0 ? (
                variants.map((variant, idx) => {
                  const variantSku = getVariantSku(variant, productId, idx);
                  const variantKey = getVariantKey(variant, productId, idx);
                  const isSelected = productSelectedVariants.includes(variantSku);
                  const manualDiscountValue = isSelected
                    ? getDisplayDiscount(variantSku, isSelected) || variant.discount || ''
                    : variant.discount || '';
                  const aiDiscountValue =
                    variant.ai_recommend_discount !== null &&
                    variant.ai_recommend_discount !== undefined &&
                    variant.ai_recommend_discount !== ''
                      ? variant.ai_recommend_discount
                      : '';
                  const discountValue = discountMode === "ai"
                    ? aiDiscountValue
                    : manualDiscountValue;
                  const aiRecommendationReason =
                    variant.ai_recommendation_data?.reason || '-';
                  
                  const variantRrp = parseFloat(variant.rrp || 0);
                  const variantCostPrice = parseFloat(variant.cost_price || 0);
                  const discountNum = discountValue === '' ? 0 : parseFloat(discountValue) || 0;
                  const priceAfterDiscount = discountNum > 0 && variantRrp > 0 
                    ? variantRrp * (1 - (discountNum / 100)) 
                    : null;
                  const isInvalidDiscount = priceAfterDiscount !== null && priceAfterDiscount <= variantCostPrice && variantCostPrice > 0;
                  
                  return (
                    <>
                      <tr key={variantKey} style={{ borderBottom: isInvalidDiscount ? "none" : "1px solid #e0e0e0" }}>
                        <td style={{ padding: "10px", textAlign: "center" }}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleSelectVariant(productId, variantSku)}
                          />
                        </td>
                        <td style={{ padding: "10px" }}>{variant.sku || "-"}</td>
                        <td style={{ padding: "10px", textAlign: "center" }}>{variant.goddiva_qty || 0}</td>
                        <td style={{ padding: "10px", textAlign: "center" }}>{variant.cg_portal_qty || 0}</td>
                        <td style={{ padding: "10px", textAlign: "center" }}>{variant.total_qty || 0}</td>
                        <td style={{ padding: "10px", textAlign: "center", color: isInvalidDiscount ? "#c62828" : "inherit" }}>£{variantCostPrice.toFixed(2)}</td>
                        <td style={{ padding: "10px", textAlign: "center" }}>£{variantRrp.toFixed(2)}</td>
                        <td style={{ padding: "10px", textAlign: "center" }}>£{parseFloat(variant.sale_price || 0).toFixed(2)}</td>
                        <td style={{ padding: "10px", textAlign: "center" }}>{variant.sold || 0}</td>
                        <td style={{ padding: "10px", textAlign: "center" }}>{variant.return || 0}</td>
                        <td style={{ padding: "10px", textAlign: "center" }}>{variant.sale || 0}</td>
                        <td style={{ padding: "10px", textAlign: "center" }}>
                          {discountMode === "ai" ? (
                            <span>{aiDiscountValue !== '' ? `${parseFloat(aiDiscountValue).toFixed(2)}%` : '-'}</span>
                          ) : isSelected ? (
                            <input
                              type="text"
                              value={discountValue}
                              onChange={(e) => {
                                const val = e.target.value;
                                if (val === '' || /^\d*\.?\d*$/.test(val)) {
                                  setLocalDiscounts(prev => ({
                                    ...prev,
                                    [variantSku]: val
                                  }));
                                }
                              }}
                              onBlur={(e) => {
                                const val = e.target.value.trim();
                                setLocalDiscounts(prev => {
                                  const { [variantSku]: _, ...rest } = prev;
                                  return rest;
                                });
                                
                                if (val === '') {
                                  handleDiscountChange(productId, variantSku, '');
                                  return;
                                }
                                
                                const numVal = parseFloat(val);
                                const finalValue = (!isNaN(numVal) && numVal >= 0 && numVal <= 100) 
                                  ? numVal.toFixed(2) 
                                  : '';
                                handleDiscountChange(productId, variantSku, finalValue);
                              }}
                              placeholder="%"
                              style={{
                                width: "60px",
                                padding: "4px",
                                textAlign: "center",
                                border: isInvalidDiscount ? "1px solid #dc3545" : "1px solid #e0e0e0",
                                borderRadius: "4px",
                                fontSize: "11px"
                              }}
                            />
                          ) : (
                            <span>{variant.discount ? parseFloat(variant.discount).toFixed(2) + '%' : '-'}</span>
                          )}
                        </td>
                        {discountMode === "ai" && (
                          <td style={{ padding: "10px", textAlign: "left", whiteSpace: "normal", lineHeight: "1.4" }}>
                            {aiRecommendationReason}
                          </td>
                        )}
                        <td style={{ padding: "10px", textAlign: "center", color: isInvalidDiscount ? "#c62828" : "inherit" }}>
                          {priceAfterDiscount !== null ? `£${priceAfterDiscount.toFixed(2)}` : '-'}
                        </td>
                      </tr>
                      {isInvalidDiscount && (
                        <tr>
                          <td colSpan="5" style={{ padding: "0", borderBottom: "1px solid #e0e0e0" }}></td>
                          <td style={{ padding: "0px 10px 4px 10px", textAlign: "center", borderBottom: "1px solid #e0e0e0" }}>
                            <div style={{ 
                              color: "#c62828", 
                              fontSize: "9px", 
                              fontWeight: "bold",
                              textAlign: "center",
                              lineHeight: "1.3",
                              marginTop: "-8px"
                            }}>
                              <div>Warning: Discount</div>
                              <div>exceeds cost price!</div>
                            </div>
                          </td>
                          <td colSpan={discountMode === "ai" ? "8" : "7"} style={{ padding: "0", borderBottom: "1px solid #e0e0e0" }}></td>
                        </tr>
                      )}
                    </>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={discountMode === "ai" ? "14" : "13"} style={{ padding: "20px", textAlign: "center", color: "#666", fontSize: "11px" }}>
                    No data available.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const openAiDetailsModal = (product) => {
    setAiDetailsProduct(product);
  };

  const closeAiDetailsModal = () => setAiDetailsProduct(null);

  const tableColumns = [
    {
      name: (
        <input
          type="checkbox"
          checked={products.length > 0 && selectedProducts.length === products.length}
          onChange={(e) => handleSelectAll(e.target.checked)}
        />
      ),
      selector: (row) => (
        <input
          type="checkbox"
          checked={selectedProducts.includes(row.id)}
          onChange={() => handleSelectProduct(row.id)}
        />
      ),
      width: "30px",
      minWidth: "30px",
      sortable: false,
      center: true,
    },
    {
      name: "Title",
      selector: (row) => (
        <div style={{ display: "flex", alignItems: "center" }}>
          <img
            src={row.image || "/default-product.png"}
            alt={row.title}
            style={{ width: "40px", height: "40px", marginRight: "10px", objectFit: "cover" }}
          />
          <span>{row.title}</span>
        </div>
      ),
      sortable: true,
      wrap: true,
      minWidth: "200px",
      grow: 3,
      id: "product_name",
    },
    {
      name: "Style",
      selector: (row) => (
        <div>
          <div>{row.style}</div>
          <div style={{ fontSize: "11px", color: "#666" }}>
            {row.status === "active" ? (
              <span style={{ color: "green" }}>(Active)</span>
            ) : (
              <span style={{ color: "red" }}>(Inactive)</span>
            )}
          </div>
        </div>
      ),
      sortable: false,
      minWidth: "120px",
      grow: 1,
    },
    {
      name: "Published Date",
      selector: (row) => row.published_date || row.main_data?.published_date || "-",
      sortable: false,
      center: true,
      minWidth: "130px",
      width: "130px",
      wrap: true,
    },
    {
      name: "Current Qty",
      selector: (row) => row.current_qty,
      sortable: true,
      center: true,
      minWidth: "80px",
      width: "80px",
      sortField: "total_qty",
    },
    {
      name: "Cost Price",
      selector: (row) => `£${parseFloat(row.cost_price || 0).toFixed(2)}`,
      sortable: false,
      right: true,
      minWidth: "85px",
      width: "85px",
    },
    {
      name: "RRP",
      selector: (row) => `£${parseFloat(row.rrp || 0).toFixed(2)}`,
      sortable: true,
      right: true,
      minWidth: "75px",
      width: "75px",
      sortField: "rrp",
    },
    {
      name: "Sale Price",
      selector: (row) => `£${parseFloat(row.sale_price || 0).toFixed(2)}`,
      sortable: true,
      right: true,
      minWidth: "85px",
      width: "85px",
      sortField: "sale_price",
    },
    {
      name: "Total Sold",
      selector: (row) => row.total_sold || 0,
      sortable: true,
      center: true,
      minWidth: "85px",
      width: "85px",
      sortField: "total_sold",
    },
    {
      name: "Total Returns",
      selector: (row) => row.total_return || 0,
      sortable: true,
      center: true,
      minWidth: "85px",
      width: "85px",
      sortField: "total_return",
    },
    {
      name: "Grade",
      selector: (row) => row.grade || "-",
      sortable: true,
      center: true,
      minWidth: "80px",
      width: "80px",
      sortField: "grade",
    },
    {
      name: "Generated By",
      selector: (row) => row.generated_by || "-",
      sortable: false,
      center: true,
      minWidth: "110px",
      width: "110px",
      wrap: true,
    },
    {
      name: "Request by",
      selector: (row) => row.requested_by || "-",
      sortable: false,
      center: true,
      minWidth: "110px",
      width: "110px",
      wrap: true,
    },
    {
      name: "Notes",
      selector: (row) => <NotesCell row={row} />,
      sortable: false,
      center: true,
      minWidth: "50px",
      width: "50px",
    },
    {
      name: "AI Details",
      selector: (row) => (
          <Button
            type="button"
            color="info"
            size="sm"
            outline
            onClick={(event) => {
              event.stopPropagation();
              openAiDetailsModal(row);
            }}
            disabled={!row.id}
            title="View AI recommendation details"
            aria-label="View AI recommendation details"
          >
            <FaRobot aria-hidden="true" />
          </Button>
      ),
      sortable: false,
      center: true,
      minWidth: "90px",
      width: "90px",
    },
    {
      name: "Tags",
      selector: (row) => {
        const isRowExpanded = expandedRows[row.id] || false;
        const hasSelectedVariants = (selectedVariants[row.id] || []).length > 0;
        const isTagSelectorDisabled = !isRowExpanded || !hasSelectedVariants;

        return (
          <div style={{ width: '200px', maxWidth: '200px', overflow: 'visible', position: 'relative' }}>
            <ProductTagSelector
              productId={row.id}
              selectedTags={productSelectedTags[row.id] || []}
              tagOptions={productTagOptions}
              onTagChange={handleTagChange}
              onCreateTag={() => handleCreateNewTag(row.id)}
              isDisabled={isTagSelectorDisabled}
            />
          </div>
        );
      },
      sortable: false,
      minWidth: "200px",
      width: "200px",
      maxWidth: "200px",
      center: true,
      wrap: false,
    },
  ];

  useEffect(() => {
    if (authData?.defaultSeller) {
      setFilterSeller(authData.defaultSeller);
    }
  }, [authData?.defaultSeller]);

  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const response = await getPriceReductionFilterOptionsApi();
        if (response?.response) {
          const data = response.response;
          if (data.categories) setCategoryOptions(data.categories);
          if (data.status) setStatusOptions(data.status);
          if (data.grade) setGradeOptions(data.grade);
          if (data.generated_by) setGeneratedByOptions(data.generated_by);
        }
      } catch (error) {
        console.error('Error fetching filter options:', error);
      }
    };
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    fetchProductTags();
  }, []);

  const handleCreateNewTag = (productId) => {
    if (productId) {
      setPendingProductIdForNewTag(productId);
    }
    dispatch(openNewDialog());
  };

  const onSubmitTagForm = (dialog, data) => {
    setButtonDisable(true);
    if (dialog.type === 'new') {
      dispatch(addProductTag({...data})).then(res => {
        setButtonDisable(false);
        if (!res.error) {
          closeTagDialog();
          const savedProductId = pendingProductIdForNewTag;
          setTimeout(() => {
            fetchProductTags().then(tagsData => {
              const newTag = tagsData?.find(tag => tag.name === data.name);
              if (savedProductId && newTag) {
                const newTagOption = {
                  value: newTag.id,
                  label: newTag.name
                };
                const currentTags = productSelectedTags[savedProductId] || [];
                if (!currentTags.some(tag => tag.value === newTag.id)) {
                  setProductSelectedTags(prev => ({
                    ...prev,
                    [savedProductId]: [...currentTags, newTagOption]
                  }));
                }
              }
              setPendingProductIdForNewTag(null);
            });
          }, 500);
        }
      });
    }
  };

  const closeTagDialog = () => {
    dispatch(closeEditDialog());
    resetTagForm(productTagDefaultValues);
  };

  const fetchProductTags = async () => {
    try {
      const response = await getProductTagApi({});
      const tagsData = response?.response?.data || response?.response || [];
      
      if (Array.isArray(tagsData)) {
        const tags = tagsData.map(tag => ({
          value: tag.id,
          label: tag.name
        }));
        setProductTagOptions(tags);
      }
      return tagsData;
    } catch (error) {
      console.error('Error fetching product tags:', error);
      return [];
    }
  };

  const handleTagChange = (productId, selectedTags) => {
    setProductSelectedTags(prev => ({
      ...prev,
      [productId]: selectedTags
    }));
  };

  const handleGlobalDiscountChange = (value) => {
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      const numVal = parseFloat(value);
      if (value === '' || (!isNaN(numVal) && numVal >= 0 && numVal <= 100)) {
        setGlobalDiscount(value);
        const updatedDiscounts = { ...variantDiscounts };
        Object.keys(selectedVariants).forEach(productId => {
          const variants = selectedVariants[productId] || [];
          variants.forEach(variantSku => {
            if (!updatedDiscounts[productId]) {
              updatedDiscounts[productId] = {};
            }
            updatedDiscounts[productId][variantSku] = value === '' ? '' : parseFloat(value).toFixed(2);
          });
        });
        setVariantDiscounts(updatedDiscounts);
      }
    }
  };

  const handleGlobalTagsChange = (selectedTags) => {
    setGlobalTags(selectedTags || []);
    const updatedTags = { ...productSelectedTags };
    Object.keys(selectedVariants).forEach(productId => {
      if ((selectedVariants[productId] || []).length > 0) {
        updatedTags[productId] = selectedTags || [];
      }
    });
    setProductSelectedTags(updatedTags);
  };

  const handleSend = async () => {
    setSendLoading(true);
    const dataToSend = [];
    
    Object.keys(selectedVariants).forEach(productId => {
      const variants = selectedVariants[productId] || [];
      const productTags = productSelectedTags[productId] || globalTags || [];
      const productDiscounts = variantDiscounts[productId] || {};
      const product = products.find(item => String(item.id) === String(productId));
      const productVariants = product?.variants || product?.variants_objects || [];
      
      variants.forEach(variantSku => {
        const selectedVariant = productVariants.find(
          variant => getVariantSku(variant, productId) === variantSku
        );
        const discount = discountMode === "ai"
          ? selectedVariant?.ai_recommend_discount
          : productDiscounts[variantSku] || globalDiscount || '';
        dataToSend.push({
          productId: parseInt(productId),
          variantSku,
          discount:
            discount !== null && discount !== undefined && discount !== ''
              ? parseFloat(discount)
              : null,
          tags: productTags.length > 0 ? productTags.map(tag => {
            const tagId = tag.value || tag.id;
            return tagId ? parseInt(tagId) : null;
          }).filter(id => id !== null) : []
        });
      });
    });

    if (dataToSend.length === 0) {
      toast.error('Please select at least one variant', {
        position: toast.POSITION.TOP_RIGHT,
      });
      setSendLoading(false);
      return;
    }

    try {
      const response = await storePriceReductionsApi({ 
        data: dataToSend,
        is_sending: true 
      });
      
      const result = response?.response || response;
      
      if (result?.status === false) {
        toast.error(result.message || 'Failed to create price reduction request', {
          position: toast.POSITION.TOP_RIGHT,
          autoClose: 6000,
        });
      } else {
        toast.success(result.message || 'Price reduction request created successfully!', {
          position: toast.POSITION.TOP_RIGHT,
        });
        setSelectedProducts([]);
        setSelectedVariants({});
        setVariantDiscounts({});
        setGlobalDiscount('');
        setGlobalTags([]);
        setExpandedRows({});
        fetchProducts(true);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to store price reductions';
      toast.error(errorMessage, {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 6000,
      });
    } finally {
      setSendLoading(false);
    }
  };

  const hasSelectedVariants = Object.keys(selectedVariants).some(
    productId => (selectedVariants[productId] || []).length > 0
  );

  const validateCheckStock = (rawValue) => {
    if (rawValue === "") {
      return { value: null, error: "" };
    }

    if (!/^\d+$/.test(rawValue)) {
      return { value: null, error: "Enter a whole number." };
    }

    const value = parseInt(rawValue, 10);
    if (isNaN(value) || value < CHECK_STOCK_MIN) {
      return { value: null, error: `Must be at least ${CHECK_STOCK_MIN}.` };
    }
    if (value > CHECK_STOCK_MAX) {
      return { value: null, error: `Must not exceed ${CHECK_STOCK_MAX}.` };
    }

    return { value, error: "" };
  };

  const fetchProducts = (silent = false) => {
    if (!silent) {
      setLoading(true);
    }
    const productIdsFromUrl = getProductIdsFromUrl();
    
    const filters = {
      page: page,
      page_size: pageSize,
      seller_id: filterSeller?.value,
      brand: filterBrand?.value,
      category: filterCategory?.value,
      status: filterStatus?.value,
      grade: filterGrade?.value,
      generated_by: filterGeneratedBy?.value,
      channel_status_id: filterChannelStatus?.value || undefined,
      additional_statuses: filterAdditionalStatuses.length > 0
        ? filterAdditionalStatuses.map((option) => option.value)
        : undefined,
      is_ai: discountMode === "ai" ? 1 : 0,
      check_stock: filterCheckStock,
      search: searchText,
      sort: filterSortBy?.value?.includes("product_name")
        ? "title" + filterSortBy.value.replace("product_name", "")
        : filterSortBy?.value?.includes("total_qty")
          ? "current_qty" + filterSortBy.value.replace("total_qty", "")
          : filterSortBy?.value?.includes("rrp")
            ? "rrp" + filterSortBy.value.replace("rrp", "")
            : filterSortBy?.value?.includes("sale_price")
              ? "sale_price" + filterSortBy.value.replace("sale_price", "")
              : filterSortBy?.value?.includes("grade")
                ? "grade" + filterSortBy.value.replace("grade", "")
                : filterSortBy?.value?.includes("total_sold")
                  ? "total_sold" + filterSortBy.value.replace("total_sold", "")
                  : filterSortBy?.value?.includes("total_return")
                    ? "total_return" + filterSortBy.value.replace("total_return", "")
                    : filterSortBy?.value || 'id-desc',
      product_ids: productIdsFromUrl.length > 0 ? productIdsFromUrl.join(',') : undefined,
    };
    
    dispatch(getPriceReductionProducts(filters))
      .then((res) => {
        const total = res.payload?.pagination?.total ?? res.payload?.total ?? 0;
        const data = res.payload?.data;
        setPriceReductionSummary(res.payload?.summary || null);

        if (res.payload && data) {
          setProducts(data);
          setTotalRows(total);

          if (productIdsFromUrl.length > 0) {
            const availableProductIds = data
              .map(product => product.id)
              .filter(id => productIdsFromUrl.includes(id));
            setSelectedProducts(availableProductIds);
          } else {
            setSelectedProducts([]);
          }

          setSelectedVariants({});
          setVariantDiscounts({});

          const initialTags = {};
          data.forEach(product => {
            if (!product.tags || !Array.isArray(product.tags)) return;

            initialTags[product.id] = product.tags
              .filter(tag => {
                if (typeof tag === 'string') return tag?.trim();
                return tag?.id && tag.id !== '' && tag.id !== '-';
              })
              .map(tag => {
                if (typeof tag === 'string') {
                  return productTagOptions.find(opt => opt.label === tag) || null;
                }
                return tag?.id && tag?.name ? { value: String(tag.id), label: tag.name } : null;
              })
              .filter(Boolean);
          });
          setProductSelectedTags(initialTags);

          const checkStockSet = filterCheckStock != null && filterCheckStock !== '';
          if (checkStockSet && total === 0) {
            setCheckStockError(CHECK_STOCK_NO_RESULTS_ERROR);
            toast.error(CHECK_STOCK_NO_RESULTS_ERROR, {
              position: toast.POSITION.TOP_RIGHT,
              autoClose: 6000,
            });
          } else {
            setCheckStockError((prev) => (
              prev === CHECK_STOCK_NO_RESULTS_ERROR ? "" : prev
            ));
          }
        } else {
          setProducts([]);
          setTotalRows(0);
          setSelectedProducts([]);
          setSelectedVariants({});
          setVariantDiscounts({});
          setProductSelectedTags({});

          const checkStockSet = filterCheckStock != null && filterCheckStock !== '';
          if (checkStockSet) {
            setCheckStockError(CHECK_STOCK_NO_RESULTS_ERROR);
            toast.error(CHECK_STOCK_NO_RESULTS_ERROR, {
              position: toast.POSITION.TOP_RIGHT,
              autoClose: 6000,
            });
          } else {
            setCheckStockError((prev) => (
              prev === CHECK_STOCK_NO_RESULTS_ERROR ? "" : prev
            ));
          }
        }
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching price reduction products:', error);
        setProducts([]);
        setTotalRows(0);
        setPriceReductionSummary(null);
        setSelectedProducts([]);
        setSelectedVariants({});
        setVariantDiscounts({});
        setLoading(false);
      });
  };

  const debouncedSearch = useCallback(
    debounce((value) => {
      setSearchText(value);
      setPage(1);
    }, 500),
    []
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  useEffect(() => {
    fetchProducts();
  }, [page, pageSize, dateRange, filterSeller, filterBrand, filterCategory, filterSortBy, filterStatus, filterCheckStock, filterGrade, filterGeneratedBy, filterChannelStatus, filterAdditionalStatuses, searchText, discountMode]);

  useEffect(() => {
    if (searchText !== searchInputValue) {
      setSearchInputValue(searchText);
    }
  }, [searchText]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(1);
  };

  const handleTableSort = (column, sortDirection) => {
    let sortValue = null;

    // Map column to sort option using sortField property
    if (column.id === "product_name") {
      sortValue = sortDirection === "asc" ? "product_name-asc" : "product_name-desc";
    } else if (column.sortField === "total_qty" || column.name === "Current Qty") {
      sortValue = sortDirection === "asc" ? "total_qty-asc" : "total_qty-desc";
    } else if (column.sortField === "rrp") {
      sortValue = sortDirection === "asc" ? "rrp-asc" : "rrp-desc";
    } else if (column.sortField === "sale_price") {
      sortValue = sortDirection === "asc" ? "sale_price-asc" : "sale_price-desc";
    } else if (column.sortField === "total_sold") {
      sortValue = sortDirection === "asc" ? "total_sold-asc" : "total_sold-desc";
    } else if (column.sortField === "total_return") {
      sortValue = sortDirection === "asc" ? "total_return-asc" : "total_return-desc";
    } else if (column.sortField === "grade") {
      sortValue = sortDirection === "asc" ? "grade-asc" : "grade-desc";
    } else if (column.name === "Generated By") {
      // Generated By column doesn't have sorting option, skip
      return;
    }

    if (sortValue) {
      const selectedOption = sortOptions.find(option => option.value === sortValue);
      setFilterSortBy(selectedOption || null);
      setPage(1);
    }
  };

  const buildExportParams = () => {
    const productIdsFromUrl = getProductIdsFromUrl();

    return {
      seller_id: filterSeller?.value,
      brand: filterBrand?.value,
      category: filterCategory?.value,
      status: filterStatus?.value,
      grade: filterGrade?.value,
      generated_by: filterGeneratedBy?.value,
      check_stock: filterCheckStock,
      search: searchText,
      sort: filterSortBy?.value?.includes("product_name")
        ? "title" + filterSortBy.value.replace("product_name", "")
        : filterSortBy?.value?.includes("total_qty")
          ? "current_qty" + filterSortBy.value.replace("total_qty", "")
          : filterSortBy?.value?.includes("rrp")
            ? "rrp" + filterSortBy.value.replace("rrp", "")
            : filterSortBy?.value?.includes("sale_price")
              ? "sale_price" + filterSortBy.value.replace("sale_price", "")
              : filterSortBy?.value?.includes("grade")
                ? "grade" + filterSortBy.value.replace("grade", "")
                : filterSortBy?.value?.includes("total_sold")
                  ? "total_sold" + filterSortBy.value.replace("total_sold", "")
                  : filterSortBy?.value?.includes("total_return")
                    ? "total_return" + filterSortBy.value.replace("total_return", "")
                    : filterSortBy?.value || 'id-desc',
      product_ids: productIdsFromUrl.length > 0 ? productIdsFromUrl.join(',') : undefined,
    };
  };

  const handleExport = async () => {
    if (checkStockError) {
      toast.error(checkStockError, {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 6000,
      });
      return;
    }

    try {
      setExportLoading(true);
      const params = buildExportParams();
      const { response } = await exportPriceReductionStockApi(params);

      const isBlobResponse = response instanceof Blob;
      const blobData = isBlobResponse ? response : response?.data;
      const contentType = isBlobResponse
        ? response?.type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        : response?.headers?.['content-type']
          || response?.headers?.get?.('content-type')
          || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

      const blob = new Blob([blobData ?? ''], {
        type: contentType,
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'price-reduction-stock.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setExportModalOpen(false);
    } catch (error) {
      let message = 'Failed to export price reductions';
      let status = error?.response?.status || 'no-response';
      try {
        if (error?.response?.data instanceof Blob) {
          const text = await error.response.data.text();
          const parsed = JSON.parse(text);
          if (parsed?.message) {
            message = parsed.message;
          }
          if (status === 404) {
            message = 'No products available with the entered stock quantity.';
          }
        } else if (error?.response?.data?.message) {
          message = error.response.data.message;
        }
      } catch (e) {
        // swallow parse errors, keep default message
      }

      if (status === 404) {
        message = 'No products available with the entered stock quantity.';
      }

      toast.error(message, {
        position: toast.POSITION.TOP_RIGHT,
      });
    } finally {
      setExportLoading(false);
      setExportModalOpen(false);
    }
  };

  return (
    <Fragment>
      <Breadcrumb title="Price Reductions" data={dataMenu} />
      <Container fluid={true} className="product-wrapper">
        <div className="product-grid">
          <div className="feature-products">
            <style>
              {`
                .price-reduction-export-btn {
                  background-color: #3e4a56 !important;
                  border-color: #3e4a56 !important;
                  color: #fff !important;
                }
                .price-reduction-export-btn:hover:not(:disabled),
                .price-reduction-export-btn:focus:not(:disabled),
                .price-reduction-export-btn:active:not(:disabled) {
                  background-color: #2d3640 !important;
                  border-color: #2d3640 !important;
                  color: #fff !important;
                }
                .product-table .rdt_TableRow {
                  transition: opacity 0.3s ease-out, transform 0.3s ease-out;
                }
                .product-table .rdt_TableHeader {
                  min-height: 40px !important;
                }
                .product-table .rdt_TableCol {
                  padding: 8px 5px !important;
                  height: auto !important;
                  min-height: 40px !important;
                  display: flex !important;
                  align-items: center !important;
                  font-size: 12px !important;
                  font-weight: bold !important;
                  color: #68646d !important;
                }
                .product-table .rdt_TableCol:first-child {
                  padding: 8px 0px !important;
                  width: 20px !important;
                  min-width: 20px !important;
                  max-width: 20px !important;
                }
                .product-table .rdt_TableCell:first-child {
                  padding: 5px 0px !important;
                  width: 20px !important;
                }
                .product-table .rdt_TableCol:nth-child(2) {
                  padding: 8px 0px !important;
                  width: 25px !important;
                  min-width: 25px !important;
                  max-width: 25px !important;
                }
                .product-table .rdt_TableCell:nth-child(2) {
                  padding: 5px 0px !important;
                  width: 25px !important;
                }
                .product-table .rdt_TableCol:first-child + .rdt_TableCol {
                  padding-left: 0px !important;
                  margin-left: 0px !important;
                }
                .product-table .rdt_TableCell:first-child + .rdt_TableCell {
                  padding-left: 0px !important;
                  margin-left: 0px !important;
                }
                .product-table .rdt_Table td:first-child,
                .product-table .rdt_Table th:first-child {
                  padding-right: 0px !important;
                  border-right: none !important;
                }
                .product-table .rdt_Table td:nth-child(2),
                .product-table .rdt_Table th:nth-child(2) {
                  padding-left: 0px !important;
                  border-left: none !important;
                }
                .product-table .rdt_TableCell {
                  font-size: 11px !important;
                  padding: 5px !important;
                  overflow: hidden !important;
                  text-overflow: ellipsis !important;
                }
                .product-table .rdt_TableRow {
                  font-size: 11px !important;
                }
                .product-table .rdt_TableCol[data-column-id="tags"],
                .product-table .rdt_TableCell[data-column-id="tags"] {
                  width: 200px !important;
                  min-width: 200px !important;
                  max-width: 200px !important;
                  overflow: hidden !important;
                }
              `}
            </style>
            <Card className="mb-3" style={{ marginBottom: "1rem" }}>
              <CardBody style={{ padding: "4px 8px" }}>
                <Row className="align-items-center" style={{ gap: "12px", flexWrap: "wrap" }}>
                  {/*<Col md="auto" style={{ paddingRight: "0", flex: "0 0 auto" }}>*/}
                  {/*  <DateRangePickerPrimary*/}
                  {/*    dateRange={dateRange}*/}
                  {/*    dateRangeChange={(date) => {*/}
                  {/*      setDateRange(date);*/}
                  {/*      setPage(1);*/}
                  {/*    }}*/}
                  {/*    defaultValue={[startDateObj, endDateObj]}*/}
                  {/*    disabledDate={afterToday()}*/}
                  {/*  />*/}
                  {/*</Col>*/}
                  <Col md="auto" style={{ paddingRight: "0", flex: "1 1 0", minWidth: "150px", marginRight: "0" }}>
                    <AsyncSelect
                      className="select2-filter"
                      isClearable
                      cacheOptions
                      defaultOptions
                      value={filterBrand}
                      placeholder="Brand"
                      loadOptions={brandPromiseOption}
                      styles={customStyles}
                      menuPortalTarget={document.body}
                      components={{
                        IndicatorSeparator: () => null,
                      }}
                      menuIsOpen={isBrandMenuOpen}
                      onMenuOpen={() => setIsBrandMenuOpen(true)}
                      onMenuClose={() => setIsBrandMenuOpen(false)}
                      onChange={(e) => {
                        setFilterBrand(e);
                        setPage(1);
                      }}
                    />
                  </Col>
                  <Col md="auto" style={{ paddingRight: "0", flex: "1 1 0", minWidth: "150px", marginRight: "0" }}>
                    <Select
                      className="select2-filter"
                      isClearable
                      value={filterCategory}
                      placeholder="Category"
                      options={categoryOptions}
                      styles={customStyles}
                      menuPortalTarget={document.body}
                      components={{
                        IndicatorSeparator: () => null,
                      }}
                      menuIsOpen={isCategoryMenuOpen}
                      onMenuOpen={() => setIsCategoryMenuOpen(true)}
                      onMenuClose={() => setIsCategoryMenuOpen(false)}
                      onChange={(e) => {
                        setFilterCategory(e);
                        setPage(1);
                      }}
                    />
                  </Col>
                  <Col md="auto" style={{ paddingRight: "0", flex: "1 1 0", minWidth: "150px", marginRight: "0" }}>
                    <Select
                      className="select2-filter"
                      isClearable
                      value={filterSortBy}
                      placeholder="Sort By"
                      options={sortOptions}
                      styles={customStyles}
                      menuPortalTarget={document.body}
                      components={{
                        IndicatorSeparator: () => null,
                      }}
                      menuIsOpen={isSortByMenuOpen}
                      onMenuOpen={() => setIsSortByMenuOpen(true)}
                      onMenuClose={() => setIsSortByMenuOpen(false)}
                      onChange={(e) => {
                        setFilterSortBy(e);
                        setPage(1);
                      }}
                    />
                  </Col>
                  <Col
                    md="auto"
                    style={{
                      paddingRight: "0",
                      flex: "1 1 0",
                      minWidth: "150px",
                      marginRight: "0",
                      position: "relative",
                    }}
                  >
                    <div style={{ width: "100%" }}>
                    <div
                      style={{
                        position: "relative",
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        height: "50px",
                        backgroundColor: "#fff",
                        border: isCheckStockFocused ? "1px solid #2684ff" : "1px solid transparent",
                        boxShadow: isCheckStockFocused ? "0 0 0 1px rgba(38, 132, 255, 0.2)" : "none",
                        borderRadius: "4px",
                        padding: "0 36px 0 12px",
                        transition: "border-color 0.15s ease, box-shadow 0.15s ease",
                      }}
                    >
                      <Input
                        type="number"
                        className="form-control"
                        placeholder="Check Stock"
                        value={checkStockInput}
                        min={CHECK_STOCK_MIN}
                        max={CHECK_STOCK_MAX}
                        onChange={(e) => {
                          const raw = e.target.value;
                          setCheckStockInput(raw);
                          const { value, error } = validateCheckStock(raw);
                          setCheckStockError(error);
                          setFilterCheckStock(value);
                          setPage(1);
                        }}
                        onFocus={() => setIsCheckStockFocused(true)}
                        onBlur={() => setIsCheckStockFocused(false)}
                        style={{
                          border: "none",
                          boxShadow: "none",
                          fontSize: "14px",
                          fontWeight: 500,
                          color: "#6c757d",
                          backgroundColor: "transparent",
                          height: "100%",
                          padding: "0",
                        }}
                      />
                      <i
                        className="fa fa-chevron-down"
                        style={{
                          position: "absolute",
                          right: "12px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          color: "#9e9e9e",
                          fontSize: "12px",
                          pointerEvents: "none",
                        }}
                      />
                    </div>
                    <div
                      style={{
                        position: "absolute",
                        top: "40px",
                        left: 0,
                        fontSize: "8px",
                        color: checkStockError ? "#dc3545" : "#999",
                        lineHeight: "1",
                        width: "100%",
                        textAlign: "center",
                      }}
                    >
                      {checkStockError || "Leave Blank to export all quantities"}
                    </div>
                  </div>
                  </Col>
                  <Col md="auto" className="ml-auto" style={{ paddingRight: "0", flex: "0 0 auto", marginRight: "20px" }}>
                    <div style={{ display: "flex", justifyContent: "flex-end", paddingTop: "8px", paddingBottom: "8px", paddingRight: "8px" }}>
                    <Button
                      className="btn btn-square price-reduction-export-btn"
                      disabled={exportLoading || !!checkStockError}
                      onClick={() => setExportModalOpen(true)}
                    >
                      {exportLoading ? (
                        <i className="fa fa-spinner fa-spin fa-lg spinner mr-1" />
                      ) : (
                        'Check Stock Excel'
                      )}
                    </Button>
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
            <Row
              className="mb-3"
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "12px",
                flexWrap: "nowrap",
                marginBottom: "1rem",
                marginLeft: "0",
                marginRight: "0",
                width: "100%",
              }}
            >
              <Col
                md="auto"
                style={{
                  paddingRight: "0",
                  paddingLeft: "0",
                  flex: "2 1 0",
                  minWidth: "380px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Form style={{ width: "100%" }}>
                  <FormGroup className="m-0">
                    <Input
                      className="form-control"
                      type="text"
                      placeholder="Search Product Name / Style /SKU / Barcode"
                      value={searchInputValue}
                      onChange={(e) => {
                        setSearchInputValue(e.target.value);
                        debouncedSearch(e.target.value);
                      }}
                    />
                    <i className="fa fa-search"></i>
                  </FormGroup>
                </Form>
              </Col>
              <Col
                md="auto"
                style={{
                  paddingRight: "0",
                  paddingLeft: "0",
                  flex: "1 1 0",
                  minWidth: "180px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <div style={{ width: "100%" }}>
                  <Select
                    className="select2-filter"
                    isClearable
                    value={filterStatus}
                    placeholder="Status"
                    options={statusOptions}
                    styles={customStyles}
                    menuPortalTarget={document.body}
                    components={{
                      IndicatorSeparator: () => null,
                    }}
                    menuIsOpen={isStatusMenuOpen}
                    onMenuOpen={() => setIsStatusMenuOpen(true)}
                    onMenuClose={() => setIsStatusMenuOpen(false)}
                    onChange={(e) => {
                      setFilterStatus(e);
                      setPage(1);
                    }}
                  />
                </div>
              </Col>
              <Col
                md="auto"
                style={{
                  paddingRight: "0",
                  paddingLeft: "0",
                  flex: "1 1 0",
                  minWidth: "210px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <div style={{ width: "100%" }}>
                  <Select
                    className="select2-filter"
                    isClearable
                    value={filterChannelStatus}
                    placeholder="Channel Status"
                    options={channelStatusOptions}
                    styles={customStyles}
                    menuPortalTarget={document.body}
                    components={{
                      IndicatorSeparator: () => null,
                    }}
                    onChange={(option) => {
                      setFilterChannelStatus(option);
                      setPage(1);
                    }}
                  />
                </div>
              </Col>
              <Col
                md="auto"
                style={{
                  paddingRight: "0",
                  paddingLeft: "0",
                  flex: "1 1 0",
                  minWidth: "230px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <div style={{ width: "100%" }}>
                  <Select
                    className="select2-filter"
                    isMulti
                    isClearable
                    closeMenuOnSelect={false}
                    value={filterAdditionalStatuses}
                    placeholder="Additional Status"
                    options={additionalStatusOptions}
                    styles={customStyles}
                    menuPortalTarget={document.body}
                    components={{
                      IndicatorSeparator: () => null,
                    }}
                    onChange={(options) => {
                      setFilterAdditionalStatuses(options || []);
                      setPage(1);
                    }}
                  />
                </div>
              </Col>
              <Col
                md="auto"
                style={{
                  paddingRight: "0",
                  paddingLeft: "0",
                  flex: "1 1 0",
                  minWidth: "180px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <div style={{ width: "100%" }}>
                  <Select
                    className="select2-filter"
                    isClearable
                    value={filterGrade}
                    placeholder="Grade"
                    options={gradeOptions}
                    styles={customStyles}
                    menuPortalTarget={document.body}
                    components={{
                      IndicatorSeparator: () => null,
                    }}
                    menuIsOpen={isGradeMenuOpen}
                    onMenuOpen={() => setIsGradeMenuOpen(true)}
                    onMenuClose={() => setIsGradeMenuOpen(false)}
                    onChange={(e) => {
                      setFilterGrade(e);
                      setPage(1);
                    }}
                  />
                </div>
              </Col>
              <Col
                md="auto"
                style={{
                  paddingRight: "0",
                  paddingLeft: "0",
                  flex: "1 1 0",
                  minWidth: "180px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <div style={{ width: "100%" }}>
                  <Select
                    className="select2-filter"
                    isClearable
                    value={filterGeneratedBy}
                    placeholder="Generated By"
                    options={generatedByOptions}
                    styles={customStyles}
                    menuPortalTarget={document.body}
                    components={{
                      IndicatorSeparator: () => null,
                    }}
                    menuIsOpen={isGeneratedByMenuOpen}
                    onMenuOpen={() => setIsGeneratedByMenuOpen(true)}
                    onMenuClose={() => setIsGeneratedByMenuOpen(false)}
                    onChange={(e) => {
                      setFilterGeneratedBy(e);
                      setPage(1);
                    }}
                  />
                </div>
              </Col>
              <Col
                md="auto"
                style={{
                  paddingRight: "0",
                  paddingLeft: "0",
                  flex: "0 0 auto",
                  minWidth: "170px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <div style={{ width: "100%" }}>
                  <label style={{ fontSize: "11px", fontWeight: "bold", color: "#68646d", marginBottom: "3px", display: "block" }}>
                    AI Recommendations
                  </label>
                  <div style={{ minHeight: "30px", display: "flex", alignItems: "center", gap: "8px" }}>
                    <span
                      style={{
                        fontSize: "12px",
                        fontWeight: discountMode === "manual" ? "bold" : "normal",
                        color: discountMode === "manual" ? "#343a40" : "#9a959f",
                      }}
                    >
                      Manual
                    </span>
                    <ReactSwitch
                      checked={discountMode === "ai"}
                      onChange={(checked) => {
                        setDiscountMode(checked ? "ai" : "manual");
                        setPage(1);
                      }}
                      onColor="#28a745"
                      offColor="#6c757d"
                      checkedIcon={false}
                      uncheckedIcon={false}
                      height={22}
                      width={44}
                      aria-label="Switch between manual and AI recommendations"
                    />
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "4px",
                        fontSize: "12px",
                        fontWeight: discountMode === "ai" ? "bold" : "normal",
                        color: discountMode === "ai" ? "#28a745" : "#9a959f",
                      }}
                    >
                      <FaRobot aria-hidden="true" /> AI
                    </span>
                  </div>
                </div>
              </Col>
            </Row>
            <PriceReductionSummaryCards summary={priceReductionSummary} />
            <Card>
              <CardBody style={{ padding: '10px' }}>
                {hasSelectedVariants && (
                  <Row style={{ gap: "12px", flexWrap: "nowrap", marginBottom: "0", padding: "0", justifyContent: "flex-end", alignItems: "flex-end", marginLeft: "0", marginRight: "0" }}>
                    {discountMode === "manual" && (
                    <Col md="auto" style={{ paddingRight: "0", paddingLeft: "0", flex: "0 0 auto", minWidth: "150px" }}>
                      <FormGroup className="m-0">
                        <label style={{ fontSize: "12px", fontWeight: "bold", color: "#68646d", marginBottom: "0px", display: "block" }}>
                          Discount(%)
                        </label>
                        <Input
                          type="text"
                          className="form-control"
                          placeholder="Enter discount"
                          value={globalDiscount}
                          onChange={(e) => handleGlobalDiscountChange(e.target.value)}
                          onBlur={(e) => {
                            const val = parseFloat(e.target.value);
                            if (!isNaN(val) && val >= 0 && val <= 100) {
                              setGlobalDiscount(val.toFixed(2));
                            } else if (e.target.value.trim() === '') {
                              setGlobalDiscount('');
                            }
                          }}
                          style={{
                            minHeight: "38px",
                            fontSize: "14px",
                            border: "1px solid #e0e0e0",
                            borderRadius: "4px"
                          }}
                        />
                      </FormGroup>
                    </Col>
                    )}
                    <Col md="auto" style={{ paddingRight: "0", paddingLeft: "0", flex: "0 0 auto", minWidth: "250px", maxWidth: "300px" }}>
                      <FormGroup className="m-0">
                        <label style={{ fontSize: "12px", fontWeight: "bold", color: "#68646d", marginBottom: "0px", display: "block" }}>
                          Tags
                        </label>
                        <div style={{ width: '100%', maxWidth: '300px', overflow: 'hidden' }}>
                          <ProductTagSelector
                            productId="global"
                            selectedTags={globalTags}
                            tagOptions={productTagOptions}
                            onTagChange={(productId, selectedTags) => {
                              handleGlobalTagsChange(selectedTags);
                            }}
                            onCreateTag={() => handleCreateNewTag(null)}
                            isDisabled={false}
                          />
                        </div>
                      </FormGroup>
                    </Col>
                    <Col md="auto" style={{ paddingRight: "0", paddingLeft: "0", flex: "0 0 auto" }}>
                      <Button
                        color="success"
                        onClick={handleSend}
                        disabled={sendLoading}
                        style={{
                          minHeight: "38px",
                          minWidth: "100px",
                          padding: "8px 32px",
                          fontSize: "14px",
                          fontWeight: "bold"
                        }}
                      >
                        {sendLoading ? (
                          <>
                            <i className="fa fa-spinner fa-spin mr-1" />
                            Sending...
                          </>
                        ) : (
                          'Send'
                        )}
                      </Button>
                    </Col>
                  </Row>
                )}
                <div className="product-table" style={{ width: '100%', overflowX: 'hidden', margin: '0', marginTop: hasSelectedVariants ? '1rem' : '2.5rem' }}>
                  {loading ? (
                    <div style={{ minHeight: 150 }}>
                      <PreLoader />
                    </div>
                  ) : (
                    <CommonDataTable
                      headerColumns={tableColumns}
                      gridData={products}
                      noAction
                      paginationServer
                      paginationTotalRows={totalRows}
                      paginationRowsPerPageOptions={[10, 25, 50, 100]}
                      paginationDefaultPage={page}
                      paginationPerPage={pageSize}
                      onChangePage={handlePageChange}
                      onChangeRowsPerPage={handlePageSizeChange}
                      onSort={handleTableSort}
                      expandableRows
                      expandableRowsComponent={ExpandedComponent}
                      onRowExpandToggled={(expanded, row) => {
                        setExpandedRows(prev => ({
                          ...prev,
                          [row.id]: expanded
                        }));
                      }}
                      expandableIcon={{
                        collapsed: <i className="fa fa-chevron-right" style={{ fontSize: "12px", color: "#68646d" }} />,
                        expanded: <i className="fa fa-chevron-down" style={{ fontSize: "12px", color: "#68646d" }} />
                      }}
                    />
                  )}
                </div>
              </CardBody>
            </Card>
          </div>
        </div>
      </Container>
      <AiRecommendationDetailsModal
        product={aiDetailsProduct}
        onClose={closeAiDetailsModal}
      />
      <Modal isOpen={exportModalOpen} toggle={() => setExportModalOpen(!exportModalOpen)}>
        <ModalHeader toggle={() => setExportModalOpen(!exportModalOpen)}>
          Stock Check Confirmation
        </ModalHeader>
        <ModalBody>
          Once you download now, these stocks will be available to download after three weeks.
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleExport} disabled={exportLoading || !!checkStockError}>
            {exportLoading ? (
              <>
                <i className="fa fa-spinner fa-spin mr-1" />
                Generating...
              </>
            ) : (
              'Generate'
            )}
          </Button>
          <Button color="secondary" onClick={() => setExportModalOpen(false)} disabled={exportLoading}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
      <ModalFormSubmit
        defaultValues={productTagDefaultValues}
        schema={productTagSchema}
        onSubmitForm={onSubmitTagForm}
        reset={resetTagForm}
        formState={tagFormState}
        handleSubmit={handleTagFormSubmit}
        title='Product Tag'
        selector='product'
        dialog={({product}) => product.productTag.addDialog}
        closeDialog={closeTagDialog}
        publish
        setValue={setTagValue}
        getValues={getTagValues}
        btnText={getTagValues('publish_at') === 1 ? 'Save & Unpublish' : 'Save & Publish'}
        publishValue={getTagValues('publish_at') === 1 ? 0 : 1}
        saveValue={getTagValues('publish_at') === 1 ? 1 : 0}
        buttonDisable={buttonDisable}
      >
        <SettingForm
          control={tagControl} 
          formText="Tag" 
          errors={tagErrors} 
          register={registerTag} 
          setValue={setTagValue}
          trigger={triggerTag} 
          getValues={getTagValues}
        />
      </ModalFormSubmit>
    </Fragment>
  );
};

export default withReducer([{ product: productReducer }])(PriceReductions);
